import { getInstanceDefinition } from '../cards/cardLookup';
import {
  getPlayerById, hasActiveForcedFightObligation, hasAssignedMonsterObligation, getAssignedMonsterInstanceId,
} from '../state/selectors';
import { resolveCombat, applyCombatConclusion } from '../combat/resolveCombat';
import { heroMeetsWeaponWeight } from '../combat/attackMath';
import { isMonsterFightEligible } from '../dungeon/fightEligibility';
import { getHandler } from '../cards/effectRegistry/index';
import {
  gainCurse, getWeaponCapacityFor, getEquippedWeaponIds,
} from '../cards/effectHelpers';

// Numenera, Rugged/Swift: "Mark 1 [non-guardian] monster in the hall." /
// "After [another player/any player] selects a monster with your mark to
// fight, [gain 1 XP / add Health +N to that monster / take a prepare
// action]." A genuinely new persistent-tag mechanic - state.meta.
// markedMonsters (not turnFlags, since a mark can outlive the marking
// player's own turn until consumed) holds { instanceId, playerId } entries.
// Only Rugged/Swift register a 'react' handler for this specific trigger,
// so this doesn't risk mis-firing an unrelated React (e.g. village-
// smuggler's, which is auto-fired elsewhere on a totally different signal).
const MARK_REACT_CARD_IDS = new Set([
  'hero-rugged-l1', 'hero-rugged-l2', 'hero-rugged-l3',
  'hero-swift-l1', 'hero-swift-l2', 'hero-swift-l3',
]);

export function markMonster(state, playerId, hallRankIndex, { nonGuardianOnly = false } = {}) {
  const targetRank = hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
  if (targetRank === -1 || !state.dungeonHall[targetRank]) return state;
  const monsterInstanceId = state.dungeonHall[targetRank];
  if (nonGuardianOnly && getInstanceDefinition(state, monsterInstanceId).traits.includes('Guardian')) return state;
  const marks = state.meta.markedMonsters || [];
  if (marks.some((m) => m.instanceId === monsterInstanceId && m.playerId === playerId)) return state;
  return { ...state, meta: { ...state.meta, markedMonsters: [...marks, { instanceId: monsterInstanceId, playerId }] } };
}

// Called from chooseMonster below, BEFORE combat resolves - Swift's own
// react ("add Health +N to that monster") needs to land before the
// Attack-vs-Health comparison happens, or it'd be pointless busywork on an
// already-decided fight.
function resolveMonsterMark(state, { monsterInstanceId, selectingPlayerId }) {
  const marks = state.meta.markedMonsters || [];
  const mark = marks.find((m) => m.instanceId === monsterInstanceId);
  if (!mark) return state;
  let nextState = { ...state, meta: { ...state.meta, markedMonsters: marks.filter((m) => m !== mark) } };
  const markingPlayer = nextState.players.find((p) => p.id === mark.playerId);
  if (!markingPlayer) return nextState;
  for (const instanceId of markingPlayer.hand) {
    const def = getInstanceDefinition(nextState, instanceId);
    if (!MARK_REACT_CARD_IDS.has(def.id)) continue;
    const handler = getHandler(def.id, 'react', 0);
    if (!handler) continue;
    nextState = handler(nextState, {
      playerId: mark.playerId, instanceId, monsterInstanceId, selectingPlayerId, choices: {},
    });
  }
  return nextState;
}

// Rulebook (p.17): "Each hero can only equip one weapon card per turn. Once
// that hero equips a weapon, he or she cannot equip another one, even if the
// weapon gets discarded or destroyed... If the hero is destroyed or loses a
// weapon, you cannot equip that weapon to another hero." That's about
// involuntary unequips only (hero leveled/destroyed, weapon destroyed by an
// effect) - the rulebook has no voluntary un-equip at all. unequipWeapon
// below is a deliberate house rule on top of that (Matt asked for it,
// framed as "arranging pieces before committing" - see its own comment) -
// once a weapon is voluntarily unequipped that way, both the hero and the
// weapon ARE free again here, unlike the involuntary case above.
export function equipWeapon(state, { playerId, heroInstanceId, weaponInstanceId }) {
  const player = getPlayerById(state, playerId);
  if (!player.hand.includes(heroInstanceId) || !player.hand.includes(weaponInstanceId)) {
    throw new Error('Both the hero and the weapon must be present in hand.');
  }
  const heroDef = getInstanceDefinition(state, heroInstanceId);
  const weaponDef = getInstanceDefinition(state, weaponInstanceId);
  if (!weaponDef.traits.includes('Weapon')) throw new Error('That card is not a weapon.');
  // Root of Corruption, Woodguard: "Cannot equip a weapon." The only hero
  // group with a per-card equip restriction, so special-cased by group id
  // (same convention as the Dwarven Bear Hammer bypass just below).
  if (heroDef.group === 'woodguard') throw new Error(`${heroDef.title} cannot equip a weapon.`);
  // Strength-vs-Weight gate (Dwarven Bear Hammer/Dart Thrower's bypass,
  // Chalice Mace's reduced threshold for Clerics) - shared with
  // attackMath.js's contributesTraits, which re-runs this SAME check live
  // during combat (a Strength-reducing Battle effect can drop a hero below
  // an already-equipped weapon's Weight - see that function's own comment).
  if (!heroMeetsWeaponWeight(heroDef, weaponDef)) {
    throw new Error(`${heroDef.title} (Strength ${heroDef.strength}) cannot equip ${weaponDef.title} (Weight ${weaponDef.weight}).`);
  }
  // Caverns of Bane, Disowned Bloodrager: "This hero can equip two weapons."
  // Every other hero's capacity is 1 (getWeaponCapacity's default) - see
  // effectHelpers.js. Numenera, Punch Dagger: "Heroes may equip 2 of this
  // card" raises that to 2 specifically when every already-equipped weapon
  // on this hero is ALSO a Punch Dagger - getWeaponCapacityFor is the same
  // check, just aware of what's actually being equipped, not just the hero.
  const alreadyEquipped = getEquippedWeaponIds(player, heroInstanceId);
  if (alreadyEquipped.length >= getWeaponCapacityFor(state, player, heroInstanceId, heroDef, weaponDef)) {
    throw new Error('This hero is already equipped with a weapon this turn.');
  }
  if (Object.values(player.equippedWeapons).flat().includes(weaponInstanceId)) {
    throw new Error('This weapon is already equipped to another hero.');
  }
  if (player.turnFlags.cannotEquipWeapons) {
    throw new Error('You cannot equip any more weapons this turn.');
  }

  const players = state.players.map((p) => (p.id === playerId
    ? { ...p, equippedWeapons: { ...p.equippedWeapons, [heroInstanceId]: [...alreadyEquipped, weaponInstanceId] } }
    : p));
  let nextState = { ...state, players };
  // Into the Abyss, Cursed Mace: "Gain 1 disease when equipped." The only
  // weapon in the pool with an on-equip trigger - equipWeapon is a single
  // choke point every equip already passes through, same place Dwarven Bear
  // Hammer/Dart Thrower's own per-weapon special cases live.
  if (weaponDef.id === 'village-cursed-mace') {
    nextState = gainCurse(nextState, playerId);
  }
  return nextState;
}

// House rule (Matt, 2026-07-14): "It would be nice to be able to unequip a
// weapon after you've equipped it as long as you haven't triggered any
// dungeon abilities that required it to be equipped." The printed rules
// have no voluntary un-equip mechanic at all (see equipWeapon's own comment)
// - this is a deliberate departure, explicitly framed as letting a player
// "toy with the arrangement" (try a weapon on one hero, reconsider, try a
// different pairing) before anything has actually depended on the choice.
// Gated on exactly that: this hero's or this weapon's own Dungeon ability
// must not have been used yet this turn (usedAbilityInstanceIds already
// tracks per-card-per-timing usage for the once-per-turn cap - reused here
// rather than adding new tracking). Deliberately does NOT also check
// foughtMonsterThisTurn: a completed fight already resolved using the
// attack total at the time, so unequipping afterward can't retroactively
// change it - it only affects future fights, which recompute attack live
// from whatever's currently equipped anyway (same as normal play).
// weaponInstanceId is optional and only needed to disambiguate when a hero
// has more than one weapon equipped (Disowned Bloodrager) - defaults to the
// most-recently-equipped one, matching describeAction's own log message
// (actions/index.js).
export function unequipWeapon(state, { playerId, heroInstanceId, weaponInstanceId }) {
  const player = getPlayerById(state, playerId);
  const equipped = getEquippedWeaponIds(player, heroInstanceId);
  if (!equipped.length) throw new Error('That hero has no equipped weapon.');
  const targetWeaponId = weaponInstanceId ?? equipped[equipped.length - 1];
  if (!equipped.includes(targetWeaponId)) throw new Error('That weapon is not equipped to this hero.');

  const dependedOnThis = player.turnFlags.usedAbilityInstanceIds.some(
    (key) => key.startsWith(`${heroInstanceId}:dungeon:`) || key.startsWith(`${targetWeaponId}:dungeon:`),
  );
  if (dependedOnThis) {
    throw new Error('A Dungeon ability that may have depended on this weapon being equipped has already been used this turn - it can no longer be unequipped.');
  }

  const players = state.players.map((p) => {
    if (p.id !== playerId) return p;
    const remaining = getEquippedWeaponIds(p, heroInstanceId).filter((id) => id !== targetWeaponId);
    const equippedWeapons = { ...p.equippedWeapons };
    if (remaining.length) equippedWeapons[heroInstanceId] = remaining;
    else delete equippedWeapons[heroInstanceId];
    return { ...p, equippedWeapons };
  });
  return { ...state, players };
}

// Fights the monster at the given hall rank (0-indexed; rank 1 = index 0). The
// combat result is stashed on meta.lastCombatResult (a transient field, not
// part of the rules) so the UI/tests can read what just happened without the
// dispatch() contract needing to return anything but the next GameState.
export function chooseMonster(state, { playerId, hallRankIndex, choices }) {
  const player = getPlayerById(state, playerId);
  // Rulebook (p.18): "your party is working together to slay ONE monster" -
  // no ToR card grants an extra fight within the same dungeon turn (the one
  // "take another turn" effect, Scabbark's Spoils ability, grants a whole
  // separate turn instead, which resets turnFlags normally via endTurn - so
  // this guard doesn't block that). Matt: "I've been able to just keep
  // clicking on monsters... it should've stopped me after the first one."
  if (player.turnFlags.foughtMonsterThisTurn) {
    throw new Error('You have already fought a monster this turn.');
  }
  // Root of Corruption, Mind Control (see effectRegistry/villages.js): a
  // monster assigned to this player lives outside the hall entirely -
  // hallRankIndex === -1 is the sentinel meaning "fight my assigned monster"
  // (it also happens to make computeDarknessPenalty/computeBaseAttack's rank
  // math naturally come out to "rank 0, Darkness 0" with no special-casing
  // needed there, since baseDarkness = hallRankIndex + 1). An obligated
  // player may not choose anything else, hall or otherwise, until it's
  // resolved.
  const hasAssignedObligation = hasAssignedMonsterObligation(state, playerId);
  if (hasAssignedObligation && hallRankIndex !== -1) {
    throw new Error('Mind Control: you must fight your assigned monster first.');
  }
  const monsterInstanceId = hallRankIndex === -1 ? getAssignedMonsterInstanceId(state, playerId) : state.dungeonHall[hallRankIndex];
  if (monsterInstanceId) {
    const monsterDef = getInstanceDefinition(state, monsterInstanceId);
    if (!isMonsterFightEligible(state, player, monsterDef, hallRankIndex)) {
      throw new Error(`${monsterDef.title} cannot be fought right now.`);
    }
  }
  // Troll Skullcracker's Raid obligation (see effectRegistry/monsters.js) -
  // an obligated player may only choose the specific monster they're
  // obligated to fight, nothing else in the hall. Skipped while an assigned-
  // monster obligation is also active (Mind Control's own check above
  // already governs in that case) - the two are independent mechanisms that
  // could in principle both be active on a 3-4 player board at once.
  const obligation = state.meta.forcedFightObligation;
  const isObligated = !hasAssignedObligation && hasActiveForcedFightObligation(state, playerId);
  if (isObligated && monsterInstanceId !== obligation.monsterInstanceId) {
    throw new Error('Troll Skullcracker: you must fight it, not another monster.');
  }
  const markedState = monsterInstanceId
    ? resolveMonsterMark(state, { monsterInstanceId, selectingPlayerId: playerId })
    : state;
  const { state: combatState, result } = resolveCombat(markedState, { playerId, hallRankIndex, choices });
  // Satisfied by fighting it at all, win or lose - "must fight," not "must
  // defeat." Only clears when THIS was the obligated fight, not some
  // unrelated monster fought after the obligation had already gone void.
  // Applied immediately regardless of whether combat just paused below -
  // this is about commitment to the fight, not its eventual outcome, and
  // every other action is blocked globally while a Battle-queued choice is
  // pending anyway (see resolveCombat.js's own comment), so there's no
  // window where clearing this early could let anything illegal happen.
  const nextState = isObligated && monsterInstanceId === obligation.monsterInstanceId
    ? { ...combatState, meta: { ...combatState.meta, forcedFightObligation: null } }
    : combatState;
  const players = nextState.players.map((p) => (p.id === playerId
    ? { ...p, turnFlags: { ...p.turnFlags, foughtMonsterThisTurn: true } }
    : p));
  const withFoughtFlag = { ...nextState, players };
  // Battle just queued a player choice (e.g. Smokeplume Drake's "Discard 2
  // cards") - resolveCombat paused before computing win/loss, so there's no
  // result yet. Win/loss stats and meta.lastCombatResult get filled in later
  // by resumeCombatIfReady (actions/index.js's dispatch()) once that choice
  // is actually answered - see resolveCombat.js's own comment for why this
  // ordering matters.
  if (!result) return withFoughtFlag;
  // monsterInstanceId is stashed here (not part of resolveCombat's own
  // `result`) so abilityActions.js's useSpoilsAbility can identify exactly
  // which card was just defeated - see the Scabbark fix there. result's own
  // hallRankIndex (not this function's own possibly-stale local of the same
  // name - see resolveCombat's own comment on why) is used for Numenera's
  // Ray Emitter Spoils ("Gain XP equal to the rank of the monster
  // defeated") - the monster is already gone from the hall by the time a
  // Spoils ability resolves, so its rank wouldn't otherwise be recoverable.
  return applyCombatConclusion(withFoughtFlag, { playerId, result, monsterInstanceId });
}
